import type {
  ConfigTipografia,
  EstiloVisual as TipoEstiloVisual,
  FamiliaFonte,
  Moldura as TipoMoldura,
  NomeFonte,
  Ornamento as TipoOrnamento,
} from '@miolos/core';
import {
  FAMILIAS_FONTE,
  DESCRICAO_FONTE_NOMEADA,
  ESPESSURA_MAXIMA_DA_MOLDURA_MM,
  ESPESSURA_MINIMA_DA_MOLDURA_MM,
  ESPESSURA_PADRAO_DA_MOLDURA_MM,
  MOLDURAS,
  ORNAMENTOS,
  ROTULO_ESCRITA,
  ROTULO_FONTE,
  ROTULO_FONTE_NOMEADA,
  ROTULO_MOLDURA,
  ROTULO_ORNAMENTO,
  ROTULO_ESTILO_VISUAL,
  DESCRICAO_ESTILO_VISUAL,
  DESCRICAO_MOLDURA,
  MUDANCAS_DO_ESTILO_VISUAL,
  ESTILOS_ESCRITA,
  ESTILOS_VISUAIS,
  TEMAS,
  aplicarEstiloVisual,
  aplicarTema,
  ehOrnamentoDePapel,
} from '@miolos/core';
import { Badge } from '@/components/ui/badge';
import { Campo } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LinhaSwitch, Slider } from '@/components/ui/switch';
import { CartaoOpcao } from '@/components/CartaoOpcao';
import { EscolhaDeFonte } from '../EscolhaDeFonte';
import { useEditor } from '../EstadoEditor';
import { AmostraCor, Grade, TituloPasso } from '../ui';
import { fontesDe } from '@/impressao/cores';
import { cn } from '@/lib/utils';

/* ------------------------------------------------------------------ */
/* 7. Tema, ornamento, tipografia e pauta                              */
/* ------------------------------------------------------------------ */

export function PassoTema() {
  const { config, paleta, definirConfig, definirTipografia, definirEscrita } = useEditor();

  return (
    <div className="space-y-6">
      <TituloPasso
        titulo="Tema, ornamento e pauta"
        descricao="O tema decide as cores. O estilo visual decide o acabamento: tipografia, ornamento e o jeito dos quadros do dia. Os dois valem para qualquer modelo."
      />

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Estilo visual</h3>
          <Badge tom="destaque">{ROTULO_ESTILO_VISUAL[config.estiloVisual]}</Badge>
        </div>

        <Grade colunas={3}>
          {ESTILOS_VISUAIS.map((estilo: TipoEstiloVisual) => (
            <CartaoOpcao
              key={estilo}
              selecionado={config.estiloVisual === estilo}
              titulo={ROTULO_ESTILO_VISUAL[estilo]}
              descricao={DESCRICAO_ESTILO_VISUAL[estilo]}
              aoSelecionar={() => definirConfig(aplicarEstiloVisual(config, estilo))}
            >
              <ul className="space-y-0.5 pt-1 text-[11px] text-muted-foreground">
                {MUDANCAS_DO_ESTILO_VISUAL[estilo].map((item) => (
                  <li key={item}>· {item}</li>
                ))}
              </ul>
            </CartaoOpcao>
          ))}
        </Grade>

        <LinhaSwitch
          rotulo="Quadros do dia em caixas"
          descricao="Desligado, cada quadro vira título e linhas soltas na folha — só os dias, sem contorno nem fundo. O estilo Contemporâneo já vem assim."
          checked={config.quadrosComCaixa}
          onCheckedChange={(valor) => definirConfig({ ...config, quadrosComCaixa: valor })}
        />
      </section>

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Temas prontos</h3>
          {config.temaId === 'PERSONALIZADO' ? <Badge tom="destaque">Paleta personalizada</Badge> : null}
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {TEMAS.map((tema) => (
            <CartaoOpcao
              key={tema.id}
              selecionado={config.temaId === tema.id}
              titulo={tema.nome}
              descricao={tema.descricao}
              aoSelecionar={() => definirConfig(aplicarTema(config, tema.id))}
            >
              <div className="flex items-center gap-2 pt-1">
                <AmostraCor cor={tema.corPrincipal} className="size-6" />
                <Badge className="text-[10px]">{ROTULO_ORNAMENTO[tema.ornamento]}</Badge>
                <Badge className="text-[10px]">{tema.esquema.replace(/_/g, ' ').toLowerCase()}</Badge>
              </div>
            </CartaoOpcao>
          ))}
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Moldura da página</h3>
          <Badge className="text-[10px]">{ROTULO_MOLDURA[config.moldura]}</Badge>
        </div>
        <p className="text-xs leading-relaxed text-muted-foreground">
          Emoldura as páginas de dia e as divisórias de mês, no espaço entre o conteúdo e a borda do
          papel. A cor sai do tema.
        </p>

        <Grade colunas={3}>
          {MOLDURAS.map((moldura: TipoMoldura) => (
            <CartaoOpcao
              key={moldura}
              selecionado={config.moldura === moldura}
              titulo={ROTULO_MOLDURA[moldura]}
              descricao={DESCRICAO_MOLDURA[moldura]}
              aoSelecionar={() => definirConfig({ ...config, moldura })}
              marca={<MiniMoldura tipo={moldura} />}
            />
          ))}
        </Grade>

        {config.moldura !== 'NENHUMA' ? (
          <div className="grid gap-4 rounded-lg border border-border bg-card p-4 sm:grid-cols-2">
            <Campo
              rotulo="Traço"
              dica={`${config.molduraEspessuraMm.toFixed(1)} mm — de ${ESPESSURA_MINIMA_DA_MOLDURA_MM} a ${ESPESSURA_MAXIMA_DA_MOLDURA_MM} mm.`}
            >
              <Slider
                value={[config.molduraEspessuraMm]}
                min={ESPESSURA_MINIMA_DA_MOLDURA_MM}
                max={ESPESSURA_MAXIMA_DA_MOLDURA_MM}
                step={0.05}
                onValueChange={([valor]) =>
                  definirConfig({ ...config, molduraEspessuraMm: valor ?? ESPESSURA_PADRAO_DA_MOLDURA_MM })
                }
              />
            </Campo>

            <div className="space-y-2">
              <span className="text-sm font-medium">Cor do traço</span>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => definirConfig({ ...config, molduraCor: null })}
                  className={cn(
                    'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                    config.molduraCor === null
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border text-muted-foreground hover:border-primary/40',
                  )}
                >
                  Do tema
                </button>
                <AmostraCor cor={paleta.destaque} className="size-6" />
                <input
                  type="color"
                  aria-label="Escolher cor da moldura"
                  value={config.molduraCor ?? paleta.destaque}
                  onChange={(evento) => definirConfig({ ...config, molduraCor: evento.target.value })}
                  className="size-8 cursor-pointer rounded-md border border-input bg-transparent p-1"
                />
                <span className="font-mono text-[11px] uppercase text-muted-foreground">
                  {config.molduraCor ?? 'tema'}
                </span>
              </div>
              <p className="text-[11px] leading-relaxed text-muted-foreground">
                A moldura entra nas páginas de dia, nas divisórias de mês e nos cadernos.
              </p>
            </div>
          </div>
        ) : null}
      </section>

      <section className="space-y-3">
        <div className="space-y-1">
          <h3 className="text-sm font-semibold">Ornamento da página</h3>
          <p className="text-[11px] leading-relaxed text-muted-foreground">
            Os quatro últimos — <strong>papel pontilhado</strong>, <strong>quadriculado leve</strong>,{' '}
            <strong>ondinhas</strong> e <strong>confete</strong> — decoram a folha inteira, por baixo da
            escrita, e não cortam nada. Dá para somar à <strong>moldura</strong>: as duas coisas se
            combinam.
          </p>
        </div>
        <Grade colunas={3}>
          {ORNAMENTOS.map((ornamento: TipoOrnamento) => (
            <CartaoOpcao
              key={ornamento}
              selecionado={config.ornamento === ornamento}
              titulo={ROTULO_ORNAMENTO[ornamento]}
              aoSelecionar={() => definirConfig({ ...config, ornamento })}
              marca={<MiniOrnamento tipo={ornamento} />}
            />
          ))}
        </Grade>
      </section>

      <section className="space-y-4 rounded-lg border border-border bg-card p-4">
        <h3 className="text-sm font-semibold">Tipografia e pauta</h3>

        <div className="grid gap-4 sm:grid-cols-2">
          <Campo rotulo="Família de fonte" dica="Fontes do sistema — imprimem sem depender de internet.">
            <Select
              value={config.tipografia.familia}
              onValueChange={(valor) => definirTipografia({ familia: valor as FamiliaFonte })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FAMILIAS_FONTE.map((familia) => (
                  <SelectItem key={familia} value={familia}>
                    {ROTULO_FONTE[familia]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Campo>

          <Campo rotulo="Estilo de escrita" dica="Vale para as áreas de anotação e para os blocos de linhas.">
            <Select
              value={config.escrita.estilo}
              onValueChange={(valor) =>
                definirEscrita({ estilo: valor as (typeof ESTILOS_ESCRITA)[number] })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ESTILOS_ESCRITA.map((estilo) => (
                  <SelectItem key={estilo} value={estilo}>
                    {ROTULO_ESCRITA[estilo]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Campo>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <EscolhaDeFonte
            papel="TITULO"
            valor={config.tipografia.fonteTitulo ?? 'SISTEMA'}
            aoEscolher={(fonte) => definirTipografia({ fonteTitulo: fonte })}
          />

          <EscolhaDeFonte
            papel="TEXTO"
            valor={config.tipografia.fonteTexto ?? 'SISTEMA'}
            aoEscolher={(fonte) => definirTipografia({ fonteTexto: fonte })}
          />
        </div>

        <AmostraDaFonte tipografia={config.tipografia} />

        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium">Altura da linha</span>
            <span className="font-mono text-xs text-muted-foreground">{config.escrita.alturaLinha} mm</span>
          </div>
          <Slider
            value={[config.escrita.alturaLinha]}
            min={4}
            max={12}
            step={0.5}
            onValueChange={([valor]) => definirEscrita({ alturaLinha: valor ?? 6 })}
          />
          <p className="text-xs text-muted-foreground">
            Referência: 5 mm para letra pequena, 6–7 mm para escrita normal, 8 mm ou mais para letra grande.
          </p>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium">Espaço extra entre linhas</span>
            <span className="font-mono text-xs text-muted-foreground">{config.escrita.espacamento} mm</span>
          </div>
          <Slider
            value={[config.escrita.espacamento]}
            min={0}
            max={3}
            step={0.25}
            onValueChange={([valor]) => definirEscrita({ espacamento: valor ?? 0 })}
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium">Escala geral do texto</span>
            <span className="font-mono text-xs text-muted-foreground">
              {Math.round(config.tipografia.escala * 100)}%
            </span>
          </div>
          <Slider
            value={[config.tipografia.escala]}
            min={0.8}
            max={1.25}
            step={0.01}
            onValueChange={([valor]) => definirTipografia({ escala: valor ?? 1 })}
          />
        </div>

        <LinhaSwitch
          id="com-data"
          rotulo="Marcadores de data nas linhas"
          descricao="Adiciona um quadradinho por linha, útil para listas e registro por data."
          checked={config.escrita.comData}
          onCheckedChange={(valor) => definirEscrita({ comData: valor })}
        />
      </section>
    </div>
  );
}

/**
 * Amostra das fontes escolhidas.
 *
 * Mostra as duas na mesma frase — título e texto —, porque a diferença só fica
 * clara vendo. O aviso é o que importa de verdade: a fonte escolhida **viaja
 * dentro do PDF**, então a gráfica recebe a mesma letra mesmo sem internet, e a
 * família escolhida acima continua valendo como reserva.
 */
function AmostraDaFonte({ tipografia }: { tipografia: ConfigTipografia }) {
  const fontes = fontesDe(tipografia);
  const titulo = tipografia.fonteTitulo ?? 'SISTEMA';
  const corpo = tipografia.fonteTexto ?? 'SISTEMA';
  const nomeada = titulo !== 'SISTEMA' || corpo !== 'SISTEMA';

  return (
    <div className="space-y-2 rounded-md border border-border bg-muted/40 p-3">
      <p style={{ fontFamily: fontes.titulo }} className="text-lg leading-tight">
        AGENDA · SETEMBRO
      </p>
      <p style={{ fontFamily: fontes.corpo }} className="text-xs leading-relaxed">
        A linha da pauta fica a 6 mm desta letra, e o texto sai nesta fonte.
      </p>
      <p className="text-[11px] leading-relaxed text-muted-foreground">
        {titulo === corpo
          ? DESCRICAO_FONTE_NOMEADA[titulo]
          : `Títulos: ${DESCRICAO_FONTE_NOMEADA[titulo]} Texto: ${DESCRICAO_FONTE_NOMEADA[corpo]}`}
      </p>
      {nomeada ? (
        <p className="text-[11px] leading-relaxed text-muted-foreground">
          A fonte escolhida vai <strong>dentro do PDF</strong> — a gráfica imprime a mesma letra, sem
          depender de internet. A família acima continua valendo como reserva.
        </p>
      ) : null}
    </div>
  );
}

function MiniMoldura({ tipo }: { tipo: TipoMoldura }) {
  const comum = 'relative size-7 rounded border border-border bg-white';

  switch (tipo) {
    case 'NENHUMA':
      return <span className={comum} />;
    case 'FILETE':
      return <span className={`${comum} border-2 border-primary/60`} />;
    case 'FILETE_DUPLO':
      return (
        <span className={`${comum} border-2 border-primary/60 p-[3px]`}>
          <span className="block size-full border border-primary/50" />
        </span>
      );
    case 'CANTOS':
      return (
        <span className={comum}>
          <span className="absolute left-0.5 top-0.5 size-2 border-l-2 border-t-2 border-primary/70" />
          <span className="absolute bottom-0.5 right-0.5 size-2 border-b-2 border-r-2 border-primary/70" />
        </span>
      );
    case 'TRACEJADA':
      return <span className={`${comum} border-2 border-dashed border-primary/60`} />;
    case 'ARCO':
    default:
      return (
        <span className={`${comum} rounded-md border-2 border-primary/60`}>
          <span className="absolute left-1/2 top-[-3px] size-2.5 -translate-x-1/2 rounded-full border-2 border-primary/60 bg-white" />
        </span>
      );
  }
}

function MiniOrnamento({ tipo }: { tipo: TipoOrnamento }) {
  const comum = 'size-7 rounded border border-border bg-white';

  // Os padrões de folha inteira têm prévia própria: mostram o padrão repetido.
  if (ehOrnamentoDePapel(tipo)) return <MiniPapel tipo={tipo} />;
  switch (tipo) {
    case 'NENHUM':
      return <span className={comum} />;
    case 'LINHA_FINA':
      return (
        <span className={`${comum} flex items-center justify-center`}>
          <span className="h-[2px] w-4 bg-primary/70" />
        </span>
      );
    case 'CANTO':
      return (
        <span className={`${comum} relative`}>
          <span className="absolute left-0.5 top-0.5 size-2 border-l border-t border-primary/70" />
          <span className="absolute bottom-0.5 right-0.5 size-2 border-b border-r border-primary/70" />
        </span>
      );
    case 'ARCO':
      return (
        <span className={`${comum} overflow-hidden`}>
          <span className="absolute -right-1 -top-1 size-6 rounded-full border border-primary/50" />
        </span>
      );
    case 'FLORAL':
      return (
        <span className={`${comum} flex items-end justify-end p-1`}>
          <span className="block h-3 w-3 rounded-full border-l border-b border-primary/60" />
        </span>
      );
    case 'GEOMETRICO':
    default:
      return (
        <span className={`${comum} relative overflow-hidden`}>
          <span className="absolute left-0 top-0 size-0 border-b-[10px] border-l-[10px] border-b-transparent border-l-primary/40" />
          <span className="absolute bottom-0.5 right-0.5 size-2 rounded-sm bg-primary/40" />
        </span>
      );
  }
}

/**
 * A prévia de cada ornamento de papel é feita com o mesmo padrão do desenho
 * impresso, só que em CSS: assim a miniatura mostra o **padrão**, e não um
 * símbolo que representa o padrão.
 */
function MiniPapel({ tipo }: { tipo: TipoOrnamento }) {
  const base = 'block size-7 rounded border border-border bg-white';

  switch (tipo) {
    case 'PONTOS':
      return (
        <span
          className={base}
          style={{
            backgroundImage: 'radial-gradient(currentColor 1px, transparent 1.2px)',
            backgroundSize: '5px 5px',
            color: 'var(--primary)',
            opacity: 0.75,
          }}
        />
      );
    case 'QUADRICULADO':
      return (
        <span
          className={base}
          style={{
            backgroundImage:
              'linear-gradient(to right, var(--primary) 1px, transparent 1px), linear-gradient(to bottom, var(--primary) 1px, transparent 1px)',
            backgroundSize: '5px 5px, 5px 5px',
            opacity: 0.4,
          }}
        />
      );
    case 'ONDAS':
      return (
        <span className={`${base} overflow-hidden`}>
          <svg viewBox="0 0 14 7" preserveAspectRatio="none" className="size-full" aria-hidden>
            <path
              d="M0 5.2 C 2.3 3.4 4.7 3.4 7 5.2 C 9.3 7 11.7 7 14 5.2"
              fill="none"
              stroke="var(--primary)"
              strokeWidth={0.25}
              opacity={0.7}
            />
          </svg>
        </span>
      );
    case 'CONFETE':
    default:
      return (
        <span className={`${base} relative overflow-hidden`}>
          <span className="absolute left-1 top-1.5 size-1.5 rounded-full bg-primary/70" />
          <span className="absolute right-1.5 top-1 size-1 rounded-full bg-destructive/60" />
          <span className="absolute left-3 bottom-1.5 size-1 rotate-45 bg-primary/40" />
          <span className="absolute right-2.5 bottom-1 size-1.5 rounded-full bg-destructive/70" />
        </span>
      );
  }
}
